import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Step,
  StepLabel,
  Stepper,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  MoreVert as MenuIcon,
  AddBox as AddBoxIcon,
  Check as CheckIcon
} from '@mui/icons-material';

interface AndroidInstallInstructionsProps {
  open: boolean;
  onClose: () => void;
}

const AndroidInstallInstructions: React.FC<AndroidInstallInstructionsProps> = ({
  open,
  onClose
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      label: 'Tap the menu button',
      description: 'Look for the three dots (⋮) in the top right corner of your Chrome browser',
      icon: <MenuIcon sx={{ fontSize: 24, color: 'white' }} />
    },
    {
      label: 'Find "Add to Home screen"',
      description: 'Scroll down in the menu and tap "Add to Home screen" or "Install app"',
      icon: <AddBoxIcon sx={{ fontSize: 24, color: 'white' }} />
    },
    {
      label: 'Confirm Installation',
      description: 'Tap "Install" or "Add" to install TruckSight to your home screen',
      icon: <CheckIcon sx={{ fontSize: 24, color: 'white' }} />
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          margin: fullScreen ? 0 : 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Typography variant="h6" component="div">
          Install TruckSight on Android
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Follow these simple steps to install TruckSight as an app on your Android device:
        </Typography>

        <Stepper orientation="vertical" sx={{ '& .MuiStepConnector-root': { ml: 1.5 } }}>
          {steps.map((step, index) => (
            <Step key={index} active>
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.light,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    {step.icon}
                  </Box>
                )}
              >
                <Box sx={{ ml: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {step.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {step.description}
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: theme.palette.warning.light,
            borderRadius: 1,
            border: `1px solid ${theme.palette.warning.main}`
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, color: 'white' }}>
            ⚠️ Note:
          </Typography>
          <Typography variant="body2" sx={{ color: 'white' }}>
            Make sure you&apos;re using Chrome browser for the best installation experience.
            Some browsers may not support PWA installation.
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: theme.palette.primary.light,
            borderRadius: 1,
            border: `1px solid ${theme.palette.primary.main}`
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, color: 'white' }}>
            💡 Pro Tip:
          </Typography>
          <Typography variant="body2" sx={{ color: 'white' }}>
            After installation, TruckSight will appear on your home screen like any other app.
            You&apos;ll get faster loading times and can use it even when offline!
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{ py: 1.5, fontSize: '1rem' }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AndroidInstallInstructions;
